/**
 * useBinding.ts — POST /api/broker/v1/bindings hook
 *
 * On trigger: sends POST with Bearer JWT, gets redirect_uri, does window.location.href.
 * On error: transitions to 'error' state so caller can show EmptyState fetch-failed.
 *
 * Design-contract §11: Binding Lifecycle
 * Ref: cycle 5 brief §3.3
 */

import { useState, useCallback } from 'react';

export type BindingState = 'idle' | 'submitting' | 'error';

export interface UseBindingResult {
  state: BindingState;
  trigger: () => Promise<void>;
}

export function useBinding(serviceId: string, token: string | null): UseBindingResult {
  const [state, setState] = useState<BindingState>('idle');

  const trigger = useCallback(async () => {
    if (!token) {
      setState('error');
      return;
    }
    setState('submitting');
    try {
      const res = await fetch('/api/broker/v1/bindings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ service_id: serviceId }),
      });
      if (!res.ok) {
        setState('error');
        return;
      }
      const { redirect_uri } = await res.json() as { redirect_uri: string };
      // Navigate — state update after this is irrelevant
      window.location.href = redirect_uri;
    } catch {
      setState('error');
    }
  }, [serviceId, token]);

  return { state, trigger };
}
